import type { ServiceLayerAuthConfig } from "./config";
import { resolveApiBase, resolveSiteOrigin } from "./config";
import { getSupabaseClient } from "./supabaseClient";

export interface StartLoginOptions {
  /**
   * Optional explicit next path (e.g. "/dashboard"). If omitted, the current
   * window.location pathname+search will be used when available.
   */
  nextPath?: string;
}

export interface AuthCallbackResult {
  ok: boolean;
  /** Final URL the SPA should navigate to (usually with ?auth=success). */
  redirectUrl?: string;
  errorCode?: string;
  errorMessage?: string;
}

function currentPathnameAndSearch(): string {
  if (typeof window === "undefined") {
    return "/";
  }
  return window.location.pathname + window.location.search;
}

/**
 * Initiate Supabase OAuth login (Azure) with the canonical redirectTo used by scout/portal:
 *   <siteOrigin>/auth/callback?next=<current-path>
 */
export async function startLogin(
  config: ServiceLayerAuthConfig,
  options: StartLoginOptions = {}
): Promise<void> {
  const supabase = getSupabaseClient(config);
  const siteOrigin = resolveSiteOrigin(config);

  const nextPath =
    options.nextPath ?? (typeof window !== "undefined"
      ? currentPathnameAndSearch()
      : "/");

  // Store next path in sessionStorage — DO NOT pass it as a query parameter
  // in redirect_to. GoTrue's redirect URL validation rejects URLs with query
  // parameters that don't exactly match the allowlist entries.
  if (typeof sessionStorage !== "undefined" && nextPath !== "/") {
    sessionStorage.setItem("auth_next_path", nextPath);
  }

  const redirectTo = `${siteOrigin}/auth/callback`;

  const { error, data } = await supabase.auth.signInWithOAuth({
    provider: "azure",
    options: {
      redirectTo,
      scopes: "email offline_access",
      skipBrowserRedirect: true,
    }
  });

  if (data?.url) {
    window.location.href = data.url;
  }

  if (error) {
    throw error;
  }
}

function parseHashParams(hash: string): URLSearchParams {
  const trimmed = hash.startsWith("#") ? hash.slice(1) : hash;
  return new URLSearchParams(trimmed);
}

/**
 * Handle the /auth/callback route logic (modeled after scout / portal AuthCallback.tsx).
 *
 * This function is written in a framework-agnostic way so it can be used from
 * React, Vue, etc. The caller is responsible for performing the final
 * navigation (e.g. window.location.replace(result.redirectUrl)).
 */
export async function handleAuthCallback(
  config: ServiceLayerAuthConfig
): Promise<AuthCallbackResult> {
  const apiBase = resolveApiBase(config);
  const siteOrigin = resolveSiteOrigin(config);
  const supabase = getSupabaseClient(config);

  if (typeof window === "undefined") {
    return {
      ok: false,
      errorCode: "NO_WINDOW",
      errorMessage: "Auth callback requires a browser environment"
    };
  }

  const searchParams = new URLSearchParams(window.location.search);

  // 1) OAuth error short-circuit
  const errorParam =
    searchParams.get("error") ||
    new URLSearchParams(window.location.hash.slice(1)).get("error");
  if (errorParam) {
    const errorDescription =
      searchParams.get("error_description") ||
      new URLSearchParams(window.location.hash.slice(1)).get(
        "error_description"
      ) ||
      "Authentication failed";
    return {
      ok: false,
      errorCode: errorParam,
      errorMessage: errorDescription
    };
  }

  // 2) Acquire session (tokens) from hash, then session, then exchangeCodeForSession
  let session: any = null;

  const hash = window.location.hash;
  if (hash) {
    const hashParams = parseHashParams(hash);
    const accessToken = hashParams.get("access_token");
    const refreshToken = hashParams.get("refresh_token");
    if (accessToken && refreshToken) {
      session = {
        access_token: accessToken,
        refresh_token: refreshToken,
        expires_at: parseInt(hashParams.get("expires_at") || "0", 10),
        expires_in: parseInt(hashParams.get("expires_in") || "3600", 10),
        provider_token: hashParams.get("provider_token"),
        provider_refresh_token: hashParams.get("provider_refresh_token"),
        user: null
      };
    }
  }

  if (!session) {
    const { data, error } = await supabase.auth.getSession();
    if (error) {
      // Ignore, fall through to exchangeCodeForSession
    } else {
      session = data.session;
    }
  }

  if (!session) {
    const code = searchParams.get("code");
    if (code) {
      const { data, error } = await supabase.auth.exchangeCodeForSession(code);
      if (error) {
        return {
          ok: false,
          errorCode: "CODE_EXCHANGE_FAILED",
          errorMessage: error.message
        };
      }
      session = data.session;
    }
  }

  if (!session?.access_token || !session?.refresh_token) {
    return {
      ok: false,
      errorCode: "MISSING_TOKENS",
      errorMessage: "Missing tokens after authentication"
    };
  }

  // Persist the session into the Supabase client's local storage so
  // supabase.auth.getSession() returns a real session on subsequent reads.
  //
  // Why this exists (BUG-SL-SCOUT-REP-CANNOT-READ-OWN-MISSIONS-2026-05-26):
  // The hash-parse path above synthesises a session object from URL fragments
  // but does NOT inform the supabase client. exchangeCodeForSession would
  // persist internally, but we run flowType:'implicit' so that branch is rare.
  // Without setSession, supabase.auth.getSession() returns null even after a
  // successful login — breaking any direct Supabase REST writer (notably
  // scout_activity_log → bug-report submit). The call is idempotent across
  // all three acquisition paths above.
  //
  // We catch failures (e.g. iOS Safari Private mode where localStorage writes
  // throw QuotaExceededError) and continue — the cookie handoff below can
  // still produce a working session via HttpOnly SL cookies. The failure is
  // surfaced via the returned sessionPersistError so the UI / next bug-report
  // can record it. This is the FE half of BUG-SL-SCOUT-REP; the iOS-specific
  // storage-blocked path is addressed by a follow-up storage-adapter fallback.
  let sessionPersistError: string | null = null;
  try {
    const { error: setErr } = await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    if (setErr) sessionPersistError = setErr.message;
  } catch (err) {
    sessionPersistError = err instanceof Error ? err.message : String(err);
  }
  if (sessionPersistError) {
    // Loud breadcrumb — appears in Sentry + scout_activity_log diagnostic snapshots.
    // Do not throw: cookie handoff is still possible and produces a working app.
    console.warn(
      "[authFlow] supabase.auth.setSession failed; continuing with cookie-only session.",
      sessionPersistError,
    );
  }

  const provider = session.user?.app_metadata?.provider || "azure";
  const provider_access_token = (session as any).provider_token || null;
  const provider_refresh_token = (session as any).provider_refresh_token || null;
  const provider_expires_in = session.expires_in ?? null;

  // 3) Cookie handoff — retry on 502/503 (Cloud Run restart during deploy)
  const handoffBody = JSON.stringify({
    access_token: session.access_token,
    refresh_token: session.refresh_token,
    provider,
    provider_access_token,
    provider_refresh_token,
    provider_expires_in,
    user: session.user
  });

  let handoffRes: Response | null = null;
  const maxHandoffAttempts = 4;
  for (let attempt = 0; attempt < maxHandoffAttempts; attempt++) {
    handoffRes = await fetch(`${apiBase}/v1/auth/cookie-handoff`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: handoffBody,
    });

    if (handoffRes.ok || (handoffRes.status !== 502 && handoffRes.status !== 503)) {
      break;
    }

    // Exponential backoff: 1s, 2s, 4s
    if (attempt < maxHandoffAttempts - 1) {
      await new Promise((r) => setTimeout(r, 1000 * Math.pow(2, attempt)));
    }
  }

  if (!handoffRes || !handoffRes.ok) {
    const txt = await handoffRes?.text().catch(() => "") ?? "";
    return {
      ok: false,
      errorCode: "COOKIE_HANDOFF_FAILED",
      errorMessage: `Cookie handoff failed: ${handoffRes?.status ?? "no response"} ${txt}`
    };
  }

  // 4) Verify session cookies with a few retries (to allow for propagation)
  let verifyOk = false;
  for (let attempt = 0; attempt < 3; attempt++) {
    const verifyRes = await fetch(`${apiBase}/v1/auth/session`, {
      credentials: "include",
      headers: { "Content-Type": "application/json" }
    });
    if (verifyRes.ok) {
      verifyOk = true;
      break;
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  // 5) Build final redirect URL with ?auth=success and next param
  // Read next path from sessionStorage (stored by startLogin) or fall back to URL param
  let nextParam = searchParams.get("next");
  if (!nextParam && typeof sessionStorage !== "undefined") {
    nextParam = sessionStorage.getItem("auth_next_path");
    sessionStorage.removeItem("auth_next_path");
  }
  // Open-redirect guard (security): `next` is attacker-controllable (URL param
  // or sessionStorage). An absolute or protocol-relative value (e.g.
  // ?next=https://evil.com or //evil.com) overrides the base in
  // `new URL(next, siteOrigin)` and would redirect off-site post-auth. Resolve
  // the target, then reject any whose origin differs from the site origin.
  const siteUrl = new URL(siteOrigin);
  let targetUrl: URL;
  try {
    targetUrl = nextParam ? new URL(nextParam, siteOrigin) : new URL(siteOrigin);
  } catch {
    targetUrl = new URL(siteOrigin);
  }
  if (targetUrl.origin !== siteUrl.origin) {
    targetUrl = new URL(siteOrigin);
  }
  targetUrl.searchParams.set("auth", verifyOk ? "success" : "pending");

  return {
    ok: true,
    redirectUrl: targetUrl.toString()
  };
}



